import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  X, 
  Eye, 
  EyeOff, 
  Tag, 
  Link, 
  Bold, 
  Italic, 
  List, 
  Hash,
  Quote,
  Code,
  Image,
  Upload,
  Trash2,
  Download,
  Menu
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { useTheme } from '../../hooks/useTheme';

interface Note {
  id: string;
  title: string;
  content: string;
  tags: string[];
  links: string[];
  createdAt: Date;
  updatedAt: Date;
  isStarred: boolean;
}

interface ImageData {
  id: string;
  url: string;
  name: string;
  size: number;
  file?: File;
}

const NoteEditor: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [note, setNote] = useState<Note>({
    id: '',
    title: '',
    content: '',
    tags: [],
    links: [],
    createdAt: new Date(),
    updatedAt: new Date(),
    isStarred: false
  });

  const [showPreview, setShowPreview] = useState(false);
  const [showMobileToolbar, setShowMobileToolbar] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [images, setImages] = useState<ImageData[]>([]);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState({ width: 300, height: 200 });
  const [showImageManager, setShowImageManager] = useState(false);

  useEffect(() => {
    if (isEditing) {
      // Load existing note
      const mockNote: Note = {
        id: id!,
        title: 'Sample Note',
        content: '# Welcome to StudyBro\n\nThis is a **markdown** editor with live preview.\n\n## Features\n\n- Real-time markdown rendering\n- Tag management\n- Note linking\n- Auto-save functionality\n- Image paste support\n\n```javascript\nconst hello = "world";\nconsole.log(hello);\n```\n\n> This is a blockquote example\n\n- [ ] Todo item 1\n- [x] Completed todo\n- [ ] Another todo\n\nYou can paste images directly from clipboard!\nTry copying an image and pressing Ctrl+V in the editor.',
        tags: ['React', 'Markdown', 'Notes'],
        links: [],
        createdAt: new Date('2025-01-15'),
        updatedAt: new Date(),
        isStarred: false
      };
      setNote(mockNote);
    }
  }, [id, isEditing]);

  // Handle clipboard paste for images
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (!textareaRef.current || document.activeElement !== textareaRef.current) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await handleImageUpload(file);
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const generateImageId = () => `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleImageUpload = async (file: File): Promise<void> => {
    try {
      // Create a data URL instead of blob URL for better compatibility
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        const imageId = generateImageId();
        
        const newImage: ImageData = {
          id: imageId,
          url: imageUrl,
          name: file.name,
          size: file.size,
          file: file
        };
        
        setImages(prev => [...prev, newImage]);
        
        // Insert markdown image syntax at cursor position using the image ID
        const textarea = textareaRef.current;
        if (textarea) {
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const imageMarkdown = `\n![${file.name}](${imageId})\n`;
          
          const newContent = note.content.substring(0, start) + imageMarkdown + note.content.substring(end);
          setNote(prev => ({ ...prev, content: newContent }));
          
          // Set cursor position after the inserted image
          setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + imageMarkdown.length, start + imageMarkdown.length);
          }, 0);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedNote = {
        ...note,
        id: note.id || Date.now().toString(),
        updatedAt: new Date()
      };
      
      setNote(updatedNote);
      console.log('Note saved:', updatedNote);
      
      if (!isEditing) {
        navigate(`/notes/${updatedNote.id}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !note.tags.includes(newTag.trim())) {
      setNote(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setNote(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const insertMarkdown = (syntax: string, placeholder: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = note.content.substring(start, end);
    const replacement = selectedText || placeholder;
    
    let newContent = '';
    let cursorOffset = 0;
    
    switch (syntax) {
      case 'bold':
        newContent = note.content.substring(0, start) + `**${replacement}**` + note.content.substring(end);
        cursorOffset = selectedText ? 0 : 2;
        break;
      case 'italic':
        newContent = note.content.substring(0, start) + `*${replacement}*` + note.content.substring(end);
        cursorOffset = selectedText ? 0 : 1;
        break;
      case 'heading':
        newContent = note.content.substring(0, start) + `# ${replacement}` + note.content.substring(end);
        cursorOffset = selectedText ? 0 : 2;
        break;
      case 'list':
        newContent = note.content.substring(0, start) + `- ${replacement}` + note.content.substring(end);
        cursorOffset = selectedText ? 0 : 2;
        break;
      case 'quote':
        newContent = note.content.substring(0, start) + `> ${replacement}` + note.content.substring(end);
        cursorOffset = selectedText ? 0 : 2;
        break;
      case 'code':
        newContent = note.content.substring(0, start) + `\`${replacement}\`` + note.content.substring(end);
        cursorOffset = selectedText ? 0 : 1;
        break;
      default:
        return;
    }
    
    setNote(prev => ({ ...prev, content: newContent }));
    
    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + replacement.length + (selectedText ? 0 : cursorOffset);
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const insertImageAtCursor = (imageId: string, imageName: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const imageMarkdown = `![${imageName}](${imageId})`;
    
    const newContent = note.content.substring(0, start) + imageMarkdown + note.content.substring(end);
    setNote(prev => ({ ...prev, content: newContent }));
    
    // Set cursor position after the inserted image
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + imageMarkdown.length, start + imageMarkdown.length);
    }, 0);
  };

  const updateImageSize = (imageId: string, newSize: { width: number; height: number }) => {
    // Update the markdown content to include size attributes
    const imageData = images.find(img => img.id === imageId);
    if (!imageData) return;

    const oldMarkdown = `![${imageData.name}](${imageId})`;
    const newMarkdown = `<img src="${imageId}" alt="${imageData.name}" width="${newSize.width}" height="${newSize.height}" />`;
    
    const updatedContent = note.content.replace(oldMarkdown, newMarkdown);
    setNote(prev => ({ ...prev, content: updatedContent }));
  };

  const removeImage = (imageId: string) => {
    // Remove from images array
    setImages(prev => prev.filter(img => img.id !== imageId));
    
    // Remove from markdown content
    const imageData = images.find(img => img.id === imageId);
    if (imageData) {
      const markdownPatterns = [
        `![${imageData.name}](${imageId})`,
        `<img src="${imageId}" alt="${imageData.name}" width="\\d+" height="\\d+" />`,
        `<img src="${imageId}" alt="${imageData.name}" />`
      ];
      
      let updatedContent = note.content;
      markdownPatterns.forEach(pattern => {
        const regex = new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        updatedContent = updatedContent.replace(regex, '');
      });
      
      setNote(prev => ({ ...prev, content: updatedContent }));
    }
  };

  const downloadImage = (imageData: ImageData) => {
    const link = document.createElement('a');
    link.href = imageData.url;
    link.download = imageData.name;
    link.click();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Custom markdown components with image resizing support
  const MarkdownComponents = {
    h1: ({children}: any) => <h1 style={{ color: colors.text, borderBottom: `2px solid ${colors.surface0}`, paddingBottom: '0.5rem' }}>{children}</h1>,
    h2: ({children}: any) => <h2 style={{ color: colors.text, marginTop: '2rem' }}>{children}</h2>,
    h3: ({children}: any) => <h3 style={{ color: colors.text, marginTop: '1.5rem' }}>{children}</h3>,
    p: ({children}: any) => <p style={{ color: colors.text, lineHeight: '1.7', marginBottom: '1rem' }}>{children}</p>,
    strong: ({children}: any) => <strong style={{ color: colors.text, fontWeight: '600' }}>{children}</strong>,
    em: ({children}: any) => <em style={{ color: colors.text }}>{children}</em>,
    code: ({children}: any) => <code style={{ backgroundColor: colors.surface0, color: colors.text, padding: '2px 4px', borderRadius: '4px' }}>{children}</code>,
    pre: ({children}: any) => <pre style={{ backgroundColor: colors.surface0, color: colors.text, padding: '12px', borderRadius: '8px', overflow: 'auto' }}>{children}</pre>,
    blockquote: ({children}: any) => <blockquote style={{ borderLeft: `4px solid ${colors.blue}`, paddingLeft: '16px', color: colors.subtext0, margin: '1rem 0' }}>{children}</blockquote>,
    ul: ({children}: any) => <ul style={{ color: colors.text, paddingLeft: '1.5rem', marginBottom: '1rem' }}>{children}</ul>,
    ol: ({children}: any) => <ol style={{ color: colors.text, paddingLeft: '1.5rem', marginBottom: '1rem' }}>{children}</ol>,
    li: ({children}: any) => <li style={{ color: colors.text, marginBottom: '0.5rem' }}>{children}</li>,
    img: ({src, alt, width, height}: any) => {
      // Find the image data by ID
      const imageData = images.find(img => img.id === src);
      const actualSrc = imageData?.url || src;
      
      return (
        <div className="relative group inline-block">
          <img 
            src={actualSrc} 
            alt={alt} 
            width={width || 300}
            height={height || 200}
            style={{ 
              maxWidth: '100%', 
              height: 'auto', 
              borderRadius: '8px', 
              margin: '1rem 0',
              cursor: 'pointer',
              border: `2px solid ${colors.surface1}`
            }} 
            onClick={() => {
              if (imageData) {
                setSelectedImageId(imageData.id);
                setImageSize({ 
                  width: width || 300, 
                  height: height || 200 
                });
              }
            }}
          />
          {selectedImageId === src && (
            <div 
              className="absolute top-0 right-0 p-2 rounded-bl-lg shadow-lg"
              style={{ backgroundColor: colors.surface0, border: `1px solid ${colors.surface1}` }}
            >
              <div className="flex items-center gap-2 text-xs">
                <input
                  type="number"
                  value={imageSize.width}
                  onChange={(e) => setImageSize(prev => ({ ...prev, width: parseInt(e.target.value) || 300 }))}
                  className="w-16 px-1 py-1 rounded text-xs"
                  style={{ backgroundColor: colors.base, color: colors.text, border: `1px solid ${colors.surface1}` }}
                  placeholder="Width"
                />
                <span style={{ color: colors.text }}>×</span>
                <input
                  type="number"
                  value={imageSize.height}
                  onChange={(e) => setImageSize(prev => ({ ...prev, height: parseInt(e.target.value) || 200 }))}
                  className="w-16 px-1 py-1 rounded text-xs"
                  style={{ backgroundColor: colors.base, color: colors.text, border: `1px solid ${colors.surface1}` }}
                  placeholder="Height"
                />
                <button
                  onClick={() => {
                    if (imageData) {
                      updateImageSize(imageData.id, imageSize);
                      setSelectedImageId(null);
                    }
                  }}
                  className="px-2 py-1 rounded text-xs text-white"
                  style={{ backgroundColor: colors.blue }}
                >
                  Apply
                </button>
                <button
                  onClick={() => setSelectedImageId(null)}
                  className="px-1 py-1 rounded text-xs"
                  style={{ backgroundColor: colors.red, color: 'white' }}
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </div>
      );
    },
    br: () => <br />,
    // Handle line breaks properly
    text: ({children}: any) => {
      if (typeof children === 'string') {
        return children.split('\n').map((line, index, array) => (
          <React.Fragment key={index}>
            {line}
            {index < array.length - 1 && <br />}
          </React.Fragment>
        ));
      }
      return children;
    }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 sm:p-4 border-b"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
          <input
            type="text"
            value={note.title}
            onChange={(e) => setNote(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Note title..."
            className="text-lg sm:text-xl font-semibold bg-transparent border-none outline-none flex-1 min-w-0"
            style={{ color: colors.text }}
          />
        </div>
        
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-2 sm:px-3 py-2 rounded-lg border transition-colors hover:opacity-80 flex items-center gap-1 sm:gap-2"
            style={{ 
              backgroundColor: colors.base,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span className="hidden sm:inline">Preview</span>
          </button>
          
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-3 sm:px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-1 sm:gap-2"
            style={{ backgroundColor: colors.blue }}
          >
            <Save className="w-4 h-4" />
            <span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Save'}</span>
          </button>
          
          <button
            onClick={() => navigate('/notes')}
            className="px-2 sm:px-3 py-2 rounded-lg border transition-colors hover:opacity-80"
            style={{ 
              backgroundColor: colors.base,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile Toolbar Toggle */}
      <div className="sm:hidden">
        <button
          onClick={() => setShowMobileToolbar(!showMobileToolbar)}
          className="w-full p-3 border-b transition-colors hover:opacity-80 flex items-center justify-between"
          style={{ 
            backgroundColor: colors.base,
            borderColor: colors.surface0,
            color: colors.text
          }}
        >
          <span>Formatting Tools</span>
          <Menu className="w-4 h-4" />
        </button>
      </div>

      {/* Toolbar */}
      <div 
        className={`${showMobileToolbar ? 'block' : 'hidden'} sm:block border-b`}
        style={{ 
          backgroundColor: colors.base,
          borderColor: colors.surface0
        }}
      >
        <div className="flex flex-wrap items-center gap-1 sm:gap-2 p-2 sm:p-3">
          <button
            onClick={() => insertMarkdown('bold', 'bold text')}
            className="p-2 rounded hover:opacity-80"
            style={{ backgroundColor: colors.surface0 }}
            title="Bold"
          >
            <Bold className="w-4 h-4" style={{ color: colors.text }} />
          </button>
          
          <button
            onClick={() => insertMarkdown('italic', 'italic text')}
            className="p-2 rounded hover:opacity-80"
            style={{ backgroundColor: colors.surface0 }}
            title="Italic"
          >
            <Italic className="w-4 h-4" style={{ color: colors.text }} />
          </button>
          
          <button
            onClick={() => insertMarkdown('heading', 'Heading')}
            className="p-2 rounded hover:opacity-80"
            style={{ backgroundColor: colors.surface0 }}
            title="Heading"
          >
            <Hash className="w-4 h-4" style={{ color: colors.text }} />
          </button>
          
          <button
            onClick={() => insertMarkdown('list', 'List item')}
            className="p-2 rounded hover:opacity-80"
            style={{ backgroundColor: colors.surface0 }}
            title="List"
          >
            <List className="w-4 h-4" style={{ color: colors.text }} />
          </button>
          
          <button
            onClick={() => insertMarkdown('quote', 'Quote text')}
            className="p-2 rounded hover:opacity-80"
            style={{ backgroundColor: colors.surface0 }}
            title="Quote"
          >
            <Quote className="w-4 h-4" style={{ color: colors.text }} />
          </button>
          
          <button
            onClick={() => insertMarkdown('code', 'code')}
            className="p-2 rounded hover:opacity-80"
            style={{ backgroundColor: colors.surface0 }}
            title="Code"
          >
            <Code className="w-4 h-4" style={{ color: colors.text }} />
          </button>

          <div className="w-px h-6 mx-1 sm:mx-2" style={{ backgroundColor: colors.surface1 }} />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded hover:opacity-80 flex items-center gap-1 sm:gap-2"
            style={{ backgroundColor: colors.surface0 }}
            title="Upload image"
          >
            <Image className="w-4 h-4" style={{ color: colors.text }} />
          </button>

          <button
            onClick={() => setShowImageManager(!showImageManager)}
            className="p-2 rounded hover:opacity-80 flex items-center gap-1 sm:gap-2"
            style={{ backgroundColor: showImageManager ? colors.blue : colors.surface0 }}
            title="Manage images"
          >
            <Upload className="w-4 h-4" style={{ color: showImageManager ? 'white' : colors.text }} />
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="ml-auto text-xs hidden sm:block" style={{ color: colors.subtext0 }}>
            Tip: Paste images directly with Ctrl+V
          </div>
        </div>
      </div>

      {/* Image Manager */}
      {showImageManager && (
        <div 
          className="p-3 sm:p-4 border-b"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium" style={{ color: colors.text }}>
              Attached Images ({images.length})
            </h3>
            <button
              onClick={() => setShowImageManager(false)}
              className="text-xs px-2 py-1 rounded"
              style={{ backgroundColor: colors.surface0, color: colors.text }}
            >
              Hide
            </button>
          </div>
          
          {images.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="p-3 rounded-lg border"
                  style={{ 
                    backgroundColor: colors.base,
                    borderColor: colors.surface1
                  }}
                >
                  <div className="flex items-start gap-3">
                    <img
                      src={image.url}
                      alt={image.name}
                      className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded"
                      style={{ border: `1px solid ${colors.surface1}` }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium truncate" style={{ color: colors.text }}>
                        {image.name}
                      </h4>
                      <p className="text-xs" style={{ color: colors.subtext0 }}>
                        {formatFileSize(image.size)}
                      </p>
                      <div className="flex items-center gap-1 mt-2">
                        <button
                          onClick={() => insertImageAtCursor(image.id, image.name)}
                          className="text-xs px-2 py-1 rounded text-white"
                          style={{ backgroundColor: colors.blue }}
                        >
                          Insert
                        </button>
                        <button
                          onClick={() => downloadImage(image)}
                          className="text-xs px-2 py-1 rounded"
                          style={{ backgroundColor: colors.surface0, color: colors.text }}
                        >
                          <Download className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => removeImage(image.id)}
                          className="text-xs px-2 py-1 rounded text-white"
                          style={{ backgroundColor: colors.red }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8">
              <Image className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-3" style={{ color: colors.subtext0 }} />
              <p className="text-sm" style={{ color: colors.subtext0 }}>
                No images attached yet. Paste or upload images to get started.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Editor Content */}
      <div className="flex-1 flex flex-col sm:flex-row">
        {/* Editor Pane */}
        <div className={`${showPreview ? 'h-1/2 sm:h-full sm:w-1/2' : 'h-full w-full'} flex flex-col`}>
          <textarea
            ref={textareaRef}
            value={note.content}
            onChange={(e) => setNote(prev => ({ ...prev, content: e.target.value }))}
            placeholder="Start writing your note...

You can:
- Type markdown syntax
- Paste images directly (Ctrl+V)
- Use the toolbar buttons
- Create links with [[Note Name]]
- Manage images with the image manager"
            className="flex-1 p-3 sm:p-4 resize-none border-none outline-none font-mono text-sm sm:text-base"
            style={{
              backgroundColor: colors.base,
              color: colors.text,
              lineHeight: '1.6'
            }}
          />
        </div>

        {/* Preview Pane */}
        {showPreview && (
          <div 
            className={`${showPreview ? 'h-1/2 sm:h-full sm:w-1/2' : 'hidden'} border-t sm:border-t-0 sm:border-l overflow-y-auto`}
            style={{ borderColor: colors.surface0 }}
          >
            <div 
              className="p-3 sm:p-4 prose prose-sm max-w-none"
              style={{ color: colors.text }}
            >
              <ReactMarkdown 
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={MarkdownComponents}
              >
                {note.content || '*Preview will appear here...*'}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>

      {/* Tags Section */}
      <div 
        className="p-3 sm:p-4 border-t"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4" style={{ color: colors.subtext0 }} />
            <span className="text-sm font-medium" style={{ color: colors.text }}>Tags:</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {note.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-1 rounded flex items-center gap-1"
                style={{ 
                  backgroundColor: colors.surface0,
                  color: colors.text
                }}
              >
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="hover:opacity-80"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
              placeholder="Add tag..."
              className="text-sm px-2 py-1 rounded border w-24 sm:w-auto"
              style={{
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
            />
            <button
              onClick={handleAddTag}
              className="text-sm px-3 py-1 rounded font-medium text-white"
              style={{ backgroundColor: colors.blue }}
            >
              Add
            </button>
          </div>

          {images.length > 0 && (
            <div className="text-xs sm:ml-auto" style={{ color: colors.subtext0 }}>
              {images.length} image{images.length !== 1 ? 's' : ''} attached
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoteEditor;